import { z } from 'zod/v4';

import { selectedFileSchemaLegacy } from '../../../widget/common/schemas/legacy';
import { legacyBag, legacyBoolean, legacyEnum, legacyJssValue, legacyNumber } from '../../../legacy-leaf';

/**
 * JSS values are `string | number` and the host writes both (paddings as
 * numbers, colours as strings), so the style bag keeps them as-is rather than
 * coercing — this schema describes what is persisted, it does not reshape it.
 */
const jssValueSchemaLegacy = legacyJssValue();

/**
 * `SpecificSectionEntityData` — the header row's own settings: its background
 * pick, how its columns align vertically and which of the three fixed slots
 * (left / center / right) are shown.
 */
export const sectionSpecificDataSchemaLegacy = z.object({
    backgroundImage: legacyBag(selectedFileSchemaLegacy),
    // Every key is optional: rows persisted before these settings existed simply
    // have no key for them — the old renderer guarded with `'showLeftColumn' in
    // data` — and the migration already defaults each one. Requiring them here
    // makes the row match no legacy variant at all, which fails the whole
    // header/footer rather than the one setting.
    verticalPosition: legacyEnum(['T', 'M', 'B']),
    showLeftColumn: legacyBoolean(),
    showCenterColumn: legacyBoolean(),
    showRightColumn: legacyBoolean(),
});

/**
 * `SpecificSectionEntityStyles` — a `Pick<JssStyle, …>` on the host, so every
 * key stays optional and loosely typed.
 */
export const sectionSpecificStylesSchemaLegacy = z.object({
    backgroundColor: jssValueSchemaLegacy,
    paddingTop: jssValueSchemaLegacy,
    paddingBottom: jssValueSchemaLegacy,
    paddingLeft: jssValueSchemaLegacy,
    paddingRight: jssValueSchemaLegacy,
    backgroundSize: jssValueSchemaLegacy,
    backgroundPosition: jssValueSchemaLegacy,
    backgroundRepeat: jssValueSchemaLegacy,
    columnGap: jssValueSchemaLegacy,
});

/**
 * Layout knobs both subtypes carry. `columns` holds column ids in the store and
 * whole column objects while the structure adapter still has the denormalized
 * tree — the same `unknown[]` treatment grid gives its `children`.
 */
export const sectionCommonShapeLegacy = {
    id: z.string(),
    blockId: z.string(),
    columns: z.array(z.unknown()),
    columnIndent: legacyNumber(),
    columnWidthFlex: legacyBoolean(),
} as const;

export type SectionSpecificDataLegacy = z.infer<typeof sectionSpecificDataSchemaLegacy>;
export type SectionSpecificStylesLegacy = z.infer<typeof sectionSpecificStylesSchemaLegacy>;
